import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { isTokenExpired } from '../utils/authUtils'; // Assuming authUtils is in utils

// Create the context
const UserContext = createContext(null);

// Helper function to get auth headers
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Create the provider component
export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to fetch user profile
  const fetchUserProfile = useCallback(async (retryCount = 0) => {
    // console.log('UserContext: Fetching user profile...'); // Removed log
    setIsLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    // Check if token exists and is not expired
    if (!token || isTokenExpired(token)) {
      // console.log('UserContext: No valid token found.'); // Removed log
      setCurrentUser(null);
      setIsLoading(false);
      // Optionally trigger logout or redirect here if needed
      // Example: logout();
      return;
    }

    // Attempt to load from localStorage first for faster UI response
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        // Basic validation: check if it's an object and has an _id
        if (typeof parsedUser === 'object' && parsedUser !== null && parsedUser._id) {
          // console.log('UserContext: Loaded user from localStorage'); // Removed log
          setCurrentUser(parsedUser);
          // Still set loading to true until API confirms, but UI can use cached data
          setIsLoading(true);
        } else {
          // console.warn('UserContext: Invalid user data found in localStorage.'); // Removed log
          localStorage.removeItem('user'); // Clear invalid data
        }
      }
    } catch (e) {
      // console.error('UserContext: Error parsing user from localStorage', e); // Removed log
      localStorage.removeItem('user'); // Clear corrupted data
    }

    // Fetch from API to get the latest data and validate
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/profile`, {
        headers: getAuthHeader(),
      });

      if (response.data && response.data._id) { // Ensure we have a valid user object
        // console.log('UserContext: Fetched user profile successfully:', response.data); // Removed log
        setCurrentUser(response.data);
        // Store the fetched user data in localStorage
        localStorage.setItem('user', JSON.stringify(response.data));
        setError(null); // Clear any previous error
      } else {
        // console.error('UserContext: Invalid or empty response from user profile API:', response.data); // Removed log
        setError('Failed to fetch valid user profile.');
        setCurrentUser(null); // Clear user if API response is invalid
        localStorage.removeItem('user'); // Clear localStorage as well
      }
    } catch (err) {
      // console.error('UserContext: Error fetching user profile API:', err); // Removed log
      // Handle specific errors like 401 Unauthorized
      if (err.response && err.response.status === 401) {
        setError('Unauthorized. Please log in again.');
        setCurrentUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Optionally trigger logout
        // logout();
      } else if (retryCount < 2) {
        // Simple retry mechanism for network errors etc.
        // console.log(`UserContext: Retrying fetchUserProfile (Attempt ${retryCount + 1})`); // Removed log
        setTimeout(() => fetchUserProfile(retryCount + 1), 3000); // Retry after 3 seconds
        return; // Don't set loading false yet
      } else {
        setError(err.message || 'Failed to fetch user profile');
        // Keep potentially stale data from localStorage if API fails after retries?
        // Or clear it? Let's clear it for consistency for now.
        // setCurrentUser(null);
        // localStorage.removeItem('user');
        // console.warn('UserContext: Keeping potentially stale user data from localStorage after API failure.'); // Removed log
      }
    } finally {
      // Only set loading to false if not retrying
      if (!(error && retryCount < 2)) {
         setIsLoading(false);
      }
    }
  }, []);

  // Fetch user profile on mount
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // Value provided by the context
  const value = {
    currentUser,
    isLoading,
    error,
    refreshUser: fetchUserProfile, // Expose refresh function
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  // If context is null during initial load or after logout, provide defaults
  if (context === null) {
    return { currentUser: null, isLoading: true, error: null, refreshUser: () => {} };
  }
  return context;
};
